import { clientOwnershipLabel, resolveOwnedClientId } from '~/lib/clientOwnership';
import { normalizeEntityTags, tagKey } from '~/lib/entityTags';
import { resolveTaskAssigneeIds } from '~/lib/taskAssignees';
import type { Task } from '~/types/models';

export type TaskGroupBy = 'none' | 'project' | 'milestone' | 'status' | 'client' | 'assignee' | 'overdue' | 'tag';
export type TaskPreset = '' | 'open' | 'completed' | 'due-today' | 'overdue' | 'due-soon' | 'no-status';

export interface TaskGroupSection {
  id: string;
  title: string;
  tasks: Task[];
}

interface TaskGroupingContext {
  projectNameById: Record<string, string>;
  clientNameById: Record<string, string>;
  assigneeNameById: Record<string, string>;
  milestoneNameByTaskId?: Record<string, string>;
  statusOrder: Map<string, number>;
  preferredOrder?: string[];
  tagsByTaskId?: Record<string, string[]>;
  now?: Date;
}

export function isTaskComplete(task: Task) {
  return ['done', 'completed'].includes(String(task.status || '').trim().toLowerCase());
}

export function filterTasksByProjectId(tasks: Task[], projectId: string) {
  const normalizedProjectId = String(projectId || '').trim();
  if (!normalizedProjectId) return [];

  return tasks.filter(task => String(task.projectId || '').trim() === normalizedProjectId);
}

export function isTaskDueToday(task: Task, now = new Date()) {
  if (!task.dueDate) return false;
  const due = new Date(task.dueDate);
  return due.getFullYear() === now.getFullYear()
    && due.getMonth() === now.getMonth()
    && due.getDate() === now.getDate();
}

export function isTaskOverdue(task: Task, now = new Date()) {
  if (!task.dueDate || isTaskComplete(task)) return false;
  const due = new Date(task.dueDate);
  due.setHours(23, 59, 59, 999);
  return due.getTime() < now.getTime();
}

export function isTaskDueSoon(task: Task, now = new Date()) {
  if (!task.dueDate || isTaskComplete(task)) return false;
  const due = new Date(task.dueDate);
  const soon = new Date(now);
  soon.setDate(now.getDate() + 7);
  return due.getTime() >= now.getTime() && due.getTime() <= soon.getTime();
}

export function filterAndSortWorkspaceTasks(
  tasks: Task[],
  preset: string,
  sortField?: string | null,
  sortDirection?: 'asc' | 'desc' | null,
  now = new Date(),
) {
  let filtered = [...tasks];
  if (preset === 'open') filtered = filtered.filter(task => !isTaskComplete(task));
  else if (preset === 'completed') filtered = filtered.filter(isTaskComplete);
  else if (preset === 'due-today') filtered = filtered.filter(task => isTaskDueToday(task, now));
  else if (preset === 'overdue') filtered = filtered.filter(task => isTaskOverdue(task, now));
  else if (preset === 'due-soon') filtered = filtered.filter(task => isTaskDueSoon(task, now));
  else if (preset === 'no-status') filtered = filtered.filter(task => !String(task.status || '').trim());

  if (!sortField || !sortDirection) return filtered;
  return filtered.sort((a, b) => {
    const aValue = a[sortField as keyof Task];
    const bValue = b[sortField as keyof Task];
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;
    const comparison = String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase());
    return sortDirection === 'asc' ? comparison : -comparison;
  });
}

function applyPreferredOrder(sections: TaskGroupSection[], preferredOrder: string[] = []) {
  if (!preferredOrder.length) return sections;
  const order = new Map(preferredOrder.map((id, index) => [id, index]));
  return [...sections].sort((a, b) => {
    const aIndex = order.get(a.id);
    const bIndex = order.get(b.id);
    if (aIndex === undefined && bIndex === undefined) return 0;
    if (aIndex === undefined) return 1;
    if (bIndex === undefined) return -1;
    return aIndex - bIndex;
  });
}

export function groupWorkspaceTasks(tasks: Task[], groupBy: TaskGroupBy, context: TaskGroupingContext) {
  const sections = new Map<string, TaskGroupSection>();
  const now = context.now || new Date();

  for (const task of tasks) {
    if (groupBy === 'tag') {
      const tags = normalizeEntityTags(context.tagsByTaskId?.[task.id]);
      const groups = tags.length
        ? tags.map(tag => ({ id: `tag:${tagKey(tag)}`, title: tag }))
        : [{ id: 'no-tag', title: 'No tag' }];

      for (const group of groups) {
        const section = sections.get(group.id) || { ...group, tasks: [] };
        section.tasks.push(task);
        sections.set(group.id, section);
      }
      continue;
    }

    let id = 'workspace';
    let title = 'General workspace tasks';
    if (groupBy === 'project') {
      id = String(task.projectId || '').trim() || 'workspace';
      title = id === 'workspace' ? 'General workspace tasks' : (context.projectNameById[id] || 'Linked project');
    } else if (groupBy === 'milestone') {
      const milestoneName = String(context.milestoneNameByTaskId?.[task.id] || '').trim();
      id = milestoneName ? `milestone:${milestoneName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}` : 'no-milestone';
      title = milestoneName || 'No milestone';
    } else if (groupBy === 'status') {
      id = String(task.status || '').trim() || 'no-status';
      title = id === 'no-status' ? 'No status' : id;
    } else if (groupBy === 'client') {
      const clientId = resolveOwnedClientId(task);
      const label = clientOwnershipLabel(task, { clientNameById: context.clientNameById });
      id = clientId || label || 'no-client';
      title = label || 'No linked client';
    } else if (groupBy === 'assignee') {
      const assigneeId = resolveTaskAssigneeIds(task)[0] || '';
      const label = assigneeId ? context.assigneeNameById[assigneeId] : String(task.assignee || '').trim();
      id = assigneeId || label || 'unassigned';
      title = label || 'Unassigned';
    } else if (groupBy === 'overdue') {
      if (isTaskComplete(task)) [id, title] = ['completed', 'Completed'];
      else if (isTaskOverdue(task, now)) [id, title] = ['overdue', 'Overdue'];
      else if (isTaskDueToday(task, now)) [id, title] = ['due-today', 'Due today'];
      else if (isTaskDueSoon(task, now)) [id, title] = ['due-soon', 'Due soon'];
      else if (!task.dueDate) [id, title] = ['no-due-date', 'No due date'];
      else [id, title] = ['scheduled-later', 'Scheduled later'];
    }

    const section = sections.get(id) || { id, title, tasks: [] };
    section.tasks.push(task);
    sections.set(id, section);
  }

  let ordered = Array.from(sections.values());
  if (groupBy === 'project') {
    ordered.sort((a, b) => a.id === 'workspace' ? 1 : b.id === 'workspace' ? -1 : a.title.localeCompare(b.title));
  } else if (groupBy === 'status') {
    ordered.sort((a, b) => {
      const aIndex = context.statusOrder.get(a.title.trim().toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
      const bIndex = context.statusOrder.get(b.title.trim().toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
      return aIndex !== bIndex ? aIndex - bIndex : a.title.localeCompare(b.title);
    });
  } else if (groupBy === 'milestone' || groupBy === 'client' || groupBy === 'assignee' || groupBy === 'tag') {
    ordered.sort((a, b) => {
      const aIsEmpty = a.title.startsWith('No ') || a.title === 'Unassigned';
      const bIsEmpty = b.title.startsWith('No ') || b.title === 'Unassigned';
      if (aIsEmpty !== bIsEmpty) return aIsEmpty ? 1 : -1;
      return a.title.localeCompare(b.title);
    });
  } else if (groupBy === 'overdue') {
    const order = ['overdue', 'due-today', 'due-soon', 'scheduled-later', 'no-due-date', 'completed'];
    ordered.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
  }

  return applyPreferredOrder(ordered, context.preferredOrder);
}
